import type { HttpError } from '@refinedev/core'
import { usePermissions, useSelect } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { AuditAction, AuditEvent, Tenant } from '@sirius/contracts/admin'
import type { Column, ColumnDef, Table } from '@tanstack/react-table'
import { createContext, useContext, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ListEmptyState } from '~/components/listEmptyState'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import {
  DataTableFilterCombobox,
  DataTableFilterDropdownDateRangePicker,
} from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { Button } from '~/components/ui/button'
import { formatDateTime } from '~/i18n/format'
import { useGameOptions } from '~/pages/cabinets/useGameOptions'
import type { AdminRole } from '~/providers/authProvider'
import {
  ACTIVITY_RESOURCE,
  PLATFORM_ACTIVITY_RESOURCE,
} from '~/providers/dataProvider'
import { useActiveTenant } from '~/tenancy/activeTenant'
import {
  actionLabel,
  actionOptions,
  entityOptions,
  formatDateRange,
} from './activityFormat'
import { ACTOR_FIELD, entityLink } from './activityLinks'
import {
  ActorCell,
  ActorFilter,
  ChangesTable,
  EntityCell,
  ExpandToggle,
  TenantCell,
} from './cells'
import {
  recordFilterValue,
  recordLabelIn,
  withoutRecordFilter,
} from './recordHistory'

type Option = { label: string; value: string }

/**
 * The tenant filter's options, apart from the columns: they load after the
 * first render, and new columns would make the table jump to page 1 (Refine's
 * `useTable` resets the page when the columns change while filters are set).
 */
const TenantOptionsContext = createContext<Option[]>([])

function TenantFilter({
  column,
  table,
}: {
  column: Column<AuditEvent>
  table: Table<AuditEvent>
}) {
  return (
    <DataTableFilterCombobox
      column={column}
      table={table}
      options={useContext(TenantOptionsContext)}
      defaultOperator="in"
      operators={['in']}
      multiple
    />
  )
}

/**
 * The host tenant's activity log (`audit.list`), for staff and the tenant's
 * owners and admins. The route is guarded by access control
 * (`src/providers/accessControl.ts`); the data provider scopes the list to
 * the host's tenant, so another tenant's log is on that tenant's host. Tenant
 * users get the tenant, location and cabinet events only (the API's
 * `TENANT_VISIBLE_AUDIT_ENTITIES`).
 */
export function ActivityList() {
  const { t } = useTranslation()
  const { data: role, isLoading } = usePermissions<AdminRole>({})
  if (isLoading) {
    return null
  }
  return (
    <ActivityTable
      resource={ACTIVITY_RESOURCE}
      title={t('activityLog.title')}
      role={role ?? 'tenant'}
    />
  )
}

/**
 * Shown while the log is narrowed to one record (a show page's "View all"):
 * no column owns that filter, so this is where it shows and clears.
 */
function RecordFilterNotice({
  label,
  onClear,
}: {
  label: string | undefined
  onClear: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      role="status"
      className="bg-muted/50 flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
    >
      <span>
        <Trans
          i18nKey="activityLog.recordFilter.historyOf"
          values={{ name: label ?? t('activityLog.recordFilter.oneRecord') }}
          components={{ name: <span className="font-medium" /> }}
        />
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
      >
        {t('activityLog.recordFilter.showAll')}
      </Button>
    </div>
  )
}

/**
 * The platform's activity log (staff only, under `/platform`): every tenant's
 * events and the platform events no tenant owns (sign-ins, users, games), with the
 * tenant of each and a tenant filter.
 */
export function PlatformActivityList() {
  const { t } = useTranslation()
  const tenants = useSelect<Tenant, HttpError>({
    resource: 'tenants',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })
  const tenantOptions = useMemo<Option[]>(
    () =>
      tenants.options.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    [tenants.options],
  )
  return (
    <ActivityTable
      resource={PLATFORM_ACTIVITY_RESOURCE}
      title={t('activityLog.platformTitle')}
      role="admin"
      tenantOptions={tenantOptions}
    />
  )
}

function ActivityTable({
  resource,
  title,
  role,
  tenantOptions,
}: {
  resource: string
  title: string
  role: AdminRole
  /** the platform log: a Tenant column, filtered by these tenants */
  tenantOptions?: Option[]
}) {
  const { t } = useTranslation()
  // a cabinet's games are ids in the log: their names, one query per page
  const { names: gameNames } = useGameOptions()
  const hostTenantId = useActiveTenant().id
  // only platform admins filter by actor
  const isAdmin = role === 'admin'
  const hasTenantColumn = tenantOptions !== undefined
  const columns = useMemo<ColumnDef<AuditEvent>[]>(
    () => [
      {
        id: 'expand',
        header: () => (
          <span className="sr-only">{t('activityLog.column.changes')}</span>
        ),
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => <ExpandToggle row={row} />,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        size: 170,
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('activityLog.column.time')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownDateRangePicker
              column={column}
              defaultOperator="between"
              formatDateRange={formatDateRange}
            />
          </div>
        ),
        cell: ({ getValue }) => formatDateTime(getValue<Date>()),
        meta: { filterOperator: 'between' },
      },
      {
        // the column id is the API's filter field
        id: ACTOR_FIELD,
        accessorFn: (event) => event.actor,
        size: 200,
        enableSorting: false,
        enableColumnFilter: isAdmin,
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('activityLog.column.actor')}
            {isAdmin && (
              <ActorFilter
                column={column}
                table={table}
              />
            )}
          </div>
        ),
        cell: ({ row, table }) => (
          <ActorCell
            event={row.original}
            table={table}
            canFilter={isAdmin}
          />
        ),
      },
      {
        id: 'action',
        accessorKey: 'action',
        size: 120,
        enableSorting: false,
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('activityLog.column.action')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={actionOptions(role, t)}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ getValue }) => actionLabel(getValue<AuditAction>(), t),
        meta: { filterOperator: 'in' },
      },
      {
        id: 'entity',
        accessorKey: 'entity',
        size: 200,
        enableSorting: false,
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('activityLog.column.entity')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={entityOptions(role, t)}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ row }) => (
          <EntityCell
            event={row.original}
            link={entityLink(row.original, { role, hostTenantId })}
          />
        ),
        meta: { filterOperator: 'in' },
      },
      ...(!hasTenantColumn
        ? []
        : [
            {
              // the column id is the API's filter field
              id: 'tenantId',
              accessorFn: (event) => event.tenant,
              size: 180,
              enableSorting: false,
              header: ({ column, table }) => (
                <div className="flex items-center gap-1">
                  {t('activityLog.column.tenant')}
                  <TenantFilter
                    column={column}
                    table={table}
                  />
                </div>
              ),
              cell: ({ row }) => <TenantCell tenant={row.original.tenant} />,
              meta: { filterOperator: 'in' },
            } satisfies ColumnDef<AuditEvent>,
          ]),
    ],
    [role, hostTenantId, hasTenantColumn, t],
  )

  const table = useTable<AuditEvent, HttpError>({
    columns,
    getRowId: (event) => event.id,
    getRowCanExpand: () => true,
    refineCoreProps: {
      resource,
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      sorters: { initial: [{ field: 'createdAt', order: 'desc' }] },
    },
  })
  const { tableQuery, filters } = table.refineCore
  const isEmpty = tableQuery.data?.total === 0
  // from the table state, not the URL: the sidebar link drops the query but
  // keeps the mounted list's filters
  const recordId = recordFilterValue(table.reactTable.getState().columnFilters)

  return (
    <ListView>
      <ListViewHeader title={title} />
      {recordId !== undefined && (
        <RecordFilterNotice
          label={recordLabelIn(tableQuery.data?.data ?? [], recordId)}
          onClear={() => table.reactTable.setColumnFilters(withoutRecordFilter)}
        />
      )}
      {isEmpty ? (
        <ListEmptyState
          resource={resource}
          label={t('activityLog.empty.label')}
          canCreate={false}
          noMatchText={t('activityLog.empty.noMatch')}
          hasFilters={filters.length > 0}
          onClearFilters={() => table.reactTable.setColumnFilters([])}
        />
      ) : (
        <TenantOptionsContext value={tenantOptions ?? []}>
          <DataTable
            table={table}
            renderExpandedRow={(row) => (
              <ChangesTable
                event={row.original}
                gameNames={gameNames}
              />
            )}
          />
        </TenantOptionsContext>
      )}
    </ListView>
  )
}
